/**
 * Condition API — Landlord Portal
 *
 * Typed client for the turnover-condition-checklist landlord surface: template editor
 * (DC-1..3), walkthrough runner (DC-3/4), evidence photos, tenant magic-link access
 * tokens, and the deduction-report compiler (DC-5).
 *
 * Every response/body type below is derived (`z.infer`) from the Zod schemas in
 * `@resida/api-types/condition` — the same schema objects
 * `apps/api/src/routes/manager/condition-walkthrough.routes.ts` (and its template /
 * deduction-report / tenant-access-token siblings) register their routes with, so a
 * schema change can't silently drift out of sync with this client (see
 * packages/api-types/README.md).
 */

import { z } from 'zod';
import {
  RoomTypeSchema,
  ItemCategorySchema,
  WalkthroughPhaseSchema,
  WalkthroughActorRoleSchema,
  CaptureModeSchema,
  WalkthroughStatusSchema,
  ItemConditionSchema,
  DeficiencyTypeSchema,
  PhotoStatusSchema,
  PhotoKindSchema,
  ReportStatusSchema,
  DeliveryStatusSchema,
  BaselineStatusSchema,
  RoomTypeDefaultSchema,
  CreateRoomBodySchema,
  UpdateRoomBodySchema,
  CreateItemBodySchema,
  UpdateItemBodySchema,
  WalkthroughSummaryResponseSchema,
  MarkItemBodySchema,
  AddCorrectionBodySchema,
  GenerateUploadUrlBodySchema,
  WalkthroughIncompleteGapSchema,
  ChecklistListResponseSchema,
  IssueTokenBodySchema,
  IssueTokenResponseSchema,
  EditLineItemBodySchema,
  ReportNotReadyGapSchema,
  type UnitRoomItemResponse,
  type UnitRoomResponse,
  type RoomTypeDefault,
  type AcknowledgmentCoverage,
  type ConditionItemPhotoResponse,
  type ConditionItemResponse,
  type ConditionCorrectionResponse,
  type ConditionPhotoResponse,
  type GenerateUploadUrlResponse,
  type DeductionLineItemResponse,
  type DeductionReportResponse,
  type ChecklistListQuery,
  type ChecklistListResponse,
} from '@resida/api-types/condition';
import { apiFetch, ApiClientError } from './api.core';
import { readFileIntoMemory } from '@resida/shared';

// ============================================================================
// ENUMS — derived from @resida/api-types/condition, which mirrors
// packages/database/schema/condition.prisma exactly.
// ============================================================================

export type RoomType = z.infer<typeof RoomTypeSchema>;
export type ItemCategory = z.infer<typeof ItemCategorySchema>;
export type WalkthroughPhase = z.infer<typeof WalkthroughPhaseSchema>;
export type WalkthroughActorRole = z.infer<typeof WalkthroughActorRoleSchema>;
export type CaptureMode = z.infer<typeof CaptureModeSchema>;
export type WalkthroughStatus = z.infer<typeof WalkthroughStatusSchema>;
export type ItemCondition = z.infer<typeof ItemConditionSchema>;
export type DeficiencyType = z.infer<typeof DeficiencyTypeSchema>;
export type PhotoStatus = z.infer<typeof PhotoStatusSchema>;
export type PhotoKind = z.infer<typeof PhotoKindSchema>;
export type ReportStatus = z.infer<typeof ReportStatusSchema>;
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;
export type BaselineStatus = z.infer<typeof BaselineStatusSchema>;

export const DEFICIENCY_TYPE_LABELS: Record<DeficiencyType, string> = {
  DAMAGE: 'Damage',
  STAIN: 'Stain',
  DIRT: 'Dirt',
  EXCESSIVE_WEAR: 'Excessive wear',
  MISSING: 'Missing',
  INOPERABLE: 'Inoperable',
  OTHER: 'Other',
};

export const ROOM_TYPE_LABELS: Record<RoomType, string> = {
  BEDROOM: 'Bedroom',
  BATHROOM: 'Bathroom',
  KITCHEN: 'Kitchen',
  LIVING_ROOM: 'Living Room',
  DINING_ROOM: 'Dining Room',
  HALLWAY: 'Hallway',
  CLOSET: 'Closet',
  GARAGE: 'Garage',
  BALCONY_PATIO: 'Balcony / Patio',
  LAUNDRY_ROOM: 'Laundry Room',
  OTHER: 'Other',
};

export const ITEM_CATEGORY_LABELS: Record<ItemCategory, string> = {
  FLOORING: 'Flooring',
  WALLS_CEILING: 'Walls / Ceiling',
  WINDOWS: 'Windows',
  DOORS: 'Doors',
  APPLIANCE: 'Appliance',
  FIXTURE: 'Fixture',
  CABINETRY_COUNTERTOP: 'Cabinetry / Countertop',
  PLUMBING: 'Plumbing',
  ELECTRICAL: 'Electrical',
  HVAC: 'HVAC',
  FURNITURE: 'Furniture',
  OTHER: 'Other',
};

// ============================================================================
// TEMPLATE — mirrors apps/api/src/routes/manager/condition-template.routes.ts
// ============================================================================

export type { UnitRoomItemResponse, UnitRoomResponse, RoomTypeDefault };

// Derived from CreateRoomBodySchema/UpdateRoomBodySchema/CreateItemBodySchema/
// UpdateItemBodySchema in @resida/api-types/condition.
export type CreateRoomBody = z.infer<typeof CreateRoomBodySchema>;
export type UpdateRoomBody = z.infer<typeof UpdateRoomBodySchema>;
export type CreateItemBody = z.infer<typeof CreateItemBodySchema>;
export type UpdateItemBody = z.infer<typeof UpdateItemBodySchema>;

export async function getRoomTypeDefaults(): Promise<{ defaults: RoomTypeDefault[] }> {
  const data = await apiFetch<{ defaults: unknown[] }>(`/landlord/api/condition/room-type-defaults`);
  return { defaults: data.defaults.map((d) => RoomTypeDefaultSchema.parse(d)) };
}

export async function getConditionTemplate(
  unitId: string,
  includeDeleted?: boolean
): Promise<{ rooms: UnitRoomResponse[] }> {
  const qs = includeDeleted ? '?includeDeleted=true' : '';
  return apiFetch(`/landlord/api/units/${unitId}/condition-template${qs}`);
}

export async function instantiateConditionTemplate(unitId: string): Promise<{ rooms: UnitRoomResponse[] }> {
  return apiFetch(`/landlord/api/units/${unitId}/condition-template/instantiate`, { method: 'POST' });
}

export async function createTemplateRoom(unitId: string, body: CreateRoomBody): Promise<{ room: UnitRoomResponse }> {
  return apiFetch(`/landlord/api/units/${unitId}/condition-template/rooms`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateTemplateRoom(roomId: string, body: UpdateRoomBody): Promise<{ room: UnitRoomResponse }> {
  return apiFetch(`/landlord/api/condition-template/rooms/${roomId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteTemplateRoom(roomId: string): Promise<{ success: true }> {
  return apiFetch(`/landlord/api/condition-template/rooms/${roomId}`, { method: 'DELETE' });
}

export async function createTemplateItem(
  roomId: string,
  body: CreateItemBody
): Promise<{ item: UnitRoomItemResponse }> {
  return apiFetch(`/landlord/api/condition-template/rooms/${roomId}/items`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateTemplateItem(
  itemId: string,
  body: UpdateItemBody
): Promise<{ item: UnitRoomItemResponse }> {
  return apiFetch(`/landlord/api/condition-template/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteTemplateItem(itemId: string): Promise<{ success: true }> {
  return apiFetch(`/landlord/api/condition-template/items/${itemId}`, { method: 'DELETE' });
}

// ============================================================================
// WALKTHROUGH — mirrors apps/api/src/routes/manager/condition-walkthrough.routes.ts
// ============================================================================

export type { AcknowledgmentCoverage };

// Runtime-parsed (not just typed) against the real server schema — keeps `createWalkthrough`
// / `getWalkthroughForLease` / `getWalkthrough` / `submitWalkthrough` / `acknowledgeWalkthrough`
// below failing loudly via `.parse()` on a producer-side field change instead of drifting
// silently (same fallback pattern as `leasing.api.ts`'s `RelayMessageRetryResponseSchema`).
export type WalkthroughSummaryResponse = z.infer<typeof WalkthroughSummaryResponseSchema>;

export type { ConditionItemResponse };
export type ConditionItemPhoto = ConditionItemPhotoResponse;

// NOTE: the manager route's ConditionCorrectionResponseSchema carries BOTH
// `authorIdentityId` and `authorTokenId` as nullable uuids (BR-4a/8b) even though a
// landlord actor is always an account identity in practice — this type previously
// hand-declared `authorIdentityId` as non-nullable with no `authorTokenId` at all,
// silently drifted from the server's actual (already-nullable) shape. Deriving from the
// hoisted schema instead of a hand-rolled mirror closes that gap.
export type { ConditionCorrectionResponse };

export type { ConditionPhotoResponse };

// Derived from MarkItemBodySchema/AddCorrectionBodySchema/GenerateUploadUrlBodySchema/
// GenerateUploadUrlResponseSchema in @resida/api-types/condition.
export type MarkItemBody = z.infer<typeof MarkItemBodySchema>;
export type AddCorrectionBody = z.infer<typeof AddCorrectionBodySchema>;
export type GenerateUploadUrlBody = z.infer<typeof GenerateUploadUrlBodySchema>;
export type { GenerateUploadUrlResponse };

export type SubmitGap = z.infer<typeof WalkthroughIncompleteGapSchema>;

/** Thrown by `submitWalkthrough` on a 422 — carries the `gaps` array the shared
 * `apiFetch`'s generic `ApiClientError` would otherwise drop (gaps lives at the top
 * level of the error body, not under `details`). */
export class WalkthroughIncompleteClientError extends Error {
  gaps: SubmitGap[];
  constructor(message: string, gaps: SubmitGap[]) {
    super(message);
    this.name = 'WalkthroughIncompleteClientError';
    this.gaps = gaps;
  }
}

/** Thrown by `createWalkthrough` when the unit's condition template has no items yet
 * (`UNIT_TEMPLATE_EMPTY`) — bypasses the shared `apiFetch` for the same reason
 * `submitWalkthrough` does (see `WalkthroughIncompleteClientError` above): the shared
 * `ApiClientError.code` is populated from the generic error-type label, not the
 * machine-readable `code` field this check needs. */
export class UnitTemplateEmptyClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnitTemplateEmptyClientError';
  }
}

export async function createWalkthrough(
  leaseId: string,
  phase: WalkthroughPhase
): Promise<{ walkthrough: WalkthroughSummaryResponse }> {
  const response = await fetch(`/landlord/api/leases/${leaseId}/condition-walkthroughs`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phase }),
  });
  const data = await response.json();
  if (!response.ok) {
    if (data.code === 'UNIT_TEMPLATE_EMPTY') {
      throw new UnitTemplateEmptyClientError(data.message || 'This unit has no condition template yet.');
    }
    throw new ApiClientError(data.message || 'Failed to create walkthrough', data.error || 'UNKNOWN_ERROR', response.status, data.details);
  }
  return { walkthrough: WalkthroughSummaryResponseSchema.parse(data.walkthrough) };
}

export async function getWalkthroughForLease(
  leaseId: string,
  phase: WalkthroughPhase,
  actorRole: WalkthroughActorRole
): Promise<{ walkthrough: WalkthroughSummaryResponse | null }> {
  const data = await apiFetch<{ walkthrough: unknown }>(
    `/landlord/api/leases/${leaseId}/condition-walkthroughs?phase=${phase}&actorRole=${actorRole}`
  );
  return { walkthrough: data.walkthrough == null ? null : WalkthroughSummaryResponseSchema.parse(data.walkthrough) };
}

export async function getWalkthrough(
  walkthroughId: string
): Promise<{ summary: WalkthroughSummaryResponse; items: ConditionItemResponse[] }> {
  const data = await apiFetch<{ summary: unknown; items: ConditionItemResponse[] }>(
    `/landlord/api/condition-walkthroughs/${walkthroughId}`
  );
  return { summary: WalkthroughSummaryResponseSchema.parse(data.summary), items: data.items };
}

export async function markConditionItem(
  walkthroughId: string,
  itemId: string,
  body: MarkItemBody
): Promise<{ item: ConditionItemResponse }> {
  return apiFetch(`/landlord/api/condition-walkthroughs/${walkthroughId}/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Submit the walkthrough. Bypasses the shared `apiFetch` on the error path only, so a
 * 422 `WalkthroughIncompleteResponseSchema` body's `gaps` array survives (the shared
 * `ApiClientError` only forwards `data.details`, and `gaps` is a top-level sibling
 * field, not nested under `details`).
 */
export async function submitWalkthrough(
  walkthroughId: string,
  options?: { overridePhotoRequirement?: boolean }
): Promise<{ walkthrough: WalkthroughSummaryResponse }> {
  const response = await fetch(`/landlord/api/condition-walkthroughs/${walkthroughId}/submit`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ overridePhotoRequirement: options?.overridePhotoRequirement ?? false }),
  });
  const data = await response.json();
  if (!response.ok) {
    if (response.status === 422 && Array.isArray(data.gaps)) {
      throw new WalkthroughIncompleteClientError(
        data.message || 'This walkthrough has items that still need attention.',
        z.array(WalkthroughIncompleteGapSchema).parse(data.gaps)
      );
    }
    throw new ApiClientError(data.message || 'Failed to submit walkthrough', data.error || 'UNKNOWN_ERROR', response.status, data.details);
  }
  return { walkthrough: WalkthroughSummaryResponseSchema.parse(data.walkthrough) };
}

export async function acknowledgeWalkthrough(
  walkthroughId: string
): Promise<{ walkthrough: WalkthroughSummaryResponse }> {
  const data = await apiFetch<{ walkthrough: unknown }>(`/landlord/api/condition-walkthroughs/${walkthroughId}/acknowledge`, { method: 'POST' });
  return { walkthrough: WalkthroughSummaryResponseSchema.parse(data.walkthrough) };
}

export async function addConditionCorrection(
  itemId: string,
  body: AddCorrectionBody
): Promise<{ correction: ConditionCorrectionResponse }> {
  return apiFetch(`/landlord/api/condition-items/${itemId}/corrections`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function generatePhotoUploadUrl(
  itemId: string,
  body: GenerateUploadUrlBody
): Promise<GenerateUploadUrlResponse> {
  return apiFetch(`/landlord/api/condition-items/${itemId}/photos/upload-url`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function confirmPhotoUpload(photoId: string): Promise<ConditionPhotoResponse> {
  return apiFetch(`/landlord/api/condition-photos/${photoId}/confirm`, { method: 'POST' });
}

/**
 * DRAFT-only hard delete of a condition photo (BR-24) — mirrors
 * `conditionPhotoService.deletePhoto`. 204 no body on success; the shared `apiFetch`
 * returns `{}` for a non-JSON response, which is discarded here.
 */
export async function deleteConditionPhoto(photoId: string): Promise<void> {
  await apiFetch(`/landlord/api/condition-photos/${photoId}`, { method: 'DELETE' });
}

/**
 * Upload a captured/selected file through the presigned S3 flow: request an upload
 * URL, PUT the file directly to S3, then confirm. Mirrors the established pattern in
 * `apps/landlord/src/hooks/useUnitPhotos.ts`.
 */
export async function uploadConditionPhoto(
  itemId: string,
  file: File,
  kind: PhotoKind,
  caption?: string
): Promise<ConditionPhotoResponse> {
  // Read first: a Photos-library file can't be re-read when fetch streams the PUT body later.
  const body = await readFileIntoMemory(file);
  const { photoId, uploadUrl } = await generatePhotoUploadUrl(itemId, {
    filename: file.name,
    contentType: file.type as GenerateUploadUrlBody['contentType'],
    kind,
    caption: caption || null,
  });

  const s3Response = await fetch(uploadUrl, {
    method: 'PUT',
    body,
    headers: { 'Content-Type': file.type },
  });
  if (!s3Response.ok) {
    throw new Error(`Photo upload to storage failed: ${s3Response.status}`);
  }

  return confirmPhotoUpload(photoId);
}

/** Authz-gated streaming proxy — same-origin `<img>`/`<a>` sends the session cookie automatically. */
export function conditionPhotoDownloadUrl(photoId: string): string {
  return `/landlord/api/condition-photos/${photoId}/download`;
}

// ============================================================================
// TENANT ACCESS TOKEN — mirrors apps/api/src/routes/manager/tenant-access-token.routes.ts
// ============================================================================

// Derived from IssueTokenBodySchema/IssueTokenResponseSchema in @resida/api-types/condition.
export type IssueTenantAccessTokenBody = z.infer<typeof IssueTokenBodySchema>;
export type IssueTenantAccessTokenResponse = z.infer<typeof IssueTokenResponseSchema>;

export async function issueTenantAccessToken(
  leaseId: string,
  body: IssueTenantAccessTokenBody = {}
): Promise<IssueTenantAccessTokenResponse> {
  return apiFetch(`/landlord/api/leases/${leaseId}/tenant-access-token`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================================================
// DEDUCTION REPORT — mirrors apps/api/src/routes/manager/deduction-report.routes.ts
// ============================================================================

export type { DeductionLineItemResponse, DeductionReportResponse };

// Derived from EditLineItemBodySchema in @resida/api-types/condition.
export type EditLineItemBody = z.infer<typeof EditLineItemBodySchema>;

// Runtime-parsed (not just typed) against the real server schema in `generateDeductionReport`
// below — same fallback pattern as `RoomTypeDefaultSchema`.
export type ReportNotReadyGap = z.infer<typeof ReportNotReadyGapSchema>;

/** Thrown by `generateDeductionReport` on a 422 — carries the `gaps` array (top-level
 * sibling field the shared `ApiClientError` would otherwise drop). */
export class ReportNotReadyClientError extends Error {
  gaps: ReportNotReadyGap[];
  constructor(message: string, gaps: ReportNotReadyGap[]) {
    super(message);
    this.name = 'ReportNotReadyClientError';
    this.gaps = gaps;
  }
}

/**
 * Read-only lookup of the lease's latest deduction report (BR-20a/DC-8) — never
 * creates one. Distinct from `compileDeductionReport` (an upsert action). Used as the
 * report page's on-mount loader and to existence-gate the archived-lease "Condition
 * Records" leg without side effects.
 */
export async function getDeductionReport(leaseId: string): Promise<{ report: DeductionReportResponse | null }> {
  return apiFetch(`/landlord/api/leases/${leaseId}/deduction-report`);
}

export async function compileDeductionReport(leaseId: string): Promise<{ report: DeductionReportResponse }> {
  return apiFetch(`/landlord/api/leases/${leaseId}/deduction-report/compile`, { method: 'POST' });
}

export async function editDeductionLineItem(
  lineItemId: string,
  body: EditLineItemBody
): Promise<{ report: DeductionReportResponse }> {
  return apiFetch(`/landlord/api/deduction-report/line-items/${lineItemId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function generateDeductionReport(
  reportId: string
): Promise<{ report: DeductionReportResponse; warnings: string[] }> {
  const response = await fetch(`/landlord/api/deduction-reports/${reportId}/generate`, {
    method: 'POST',
    credentials: 'include',
  });
  const data = await response.json();
  if (!response.ok) {
    if (response.status === 422 && Array.isArray(data.gaps)) {
      throw new ReportNotReadyClientError(
        data.message || 'This report is not ready to generate yet.',
        z.array(ReportNotReadyGapSchema).parse(data.gaps)
      );
    }
    throw new ApiClientError(data.message || 'Failed to generate report', data.error || 'UNKNOWN_ERROR', response.status, data.details);
  }
  return data;
}

export async function sendDeductionReport(reportId: string): Promise<{ report: DeductionReportResponse }> {
  return apiFetch(`/landlord/api/deduction-reports/${reportId}/send`, { method: 'POST' });
}

export async function retryDeductionReportDelivery(reportId: string): Promise<{ report: DeductionReportResponse }> {
  return apiFetch(`/landlord/api/deduction-reports/${reportId}/retry-delivery`, { method: 'POST' });
}

export function deductionReportPdfUrl(reportId: string): string {
  return `/landlord/api/deduction-reports/${reportId}/pdf`;
}

export function deductionLineItemReceiptUrl(reportId: string, lineItemId: string): string {
  return `/landlord/api/deduction-reports/${reportId}/line-items/${lineItemId}/receipt`;
}

// ============================================================================
// CHECKLISTS LIST — GET /landlord/api/condition-checklists (DC-12 / BR-34)
// ============================================================================

export type ChecklistRow = ChecklistListResponse['items'][number];
export type ChecklistState = ChecklistRow['tenant']['state'];
export type ChecklistListParams = Partial<Omit<ChecklistListQuery, 'limit'>> & { limit?: number };

/** Runtime-parsed so a producer-side field change fails loudly instead of drifting. */
export async function listChecklists(query: ChecklistListParams = {}): Promise<ChecklistListResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  const data = await apiFetch<unknown>(`/landlord/api/condition-checklists${qs ? `?${qs}` : ''}`);
  return ChecklistListResponseSchema.parse(data);
}
